<template>
  <v-card class="device-config-parent">
    <v-container class="device-content">
      <v-icon class="close-icon" color="red" @click="closeWindow">mdi-close</v-icon>
      <v-row>
        <v-col cols="12" class="title-bar">
          <span>Device Configuration: {{ targetTitle }}</span>
        </v-col>
      </v-row>
      <v-form 
        ref="form"
        v-model="valid"
      >
        <v-tabs v-model="selectedTab" vertical class="tab-container" hide-slider>
          <v-tab v-for="(tab, index) in configTabs"
            :key="index"
            class="devive-config-tab"
            :class="{hidden: !tab.supported}"
          >
            <div v-if="tab.supported">
              <v-icon left>
                {{ tab.icon }}
              </v-icon>
              {{ tab.label }}
            </div>
            
          </v-tab>
          
          <v-tab-item 
            v-for="(tab, index) in configTabs" 
            :key="index"
            class="device-tab-content"
          >
              <component 
                :is="tab.comp" 
                v-if="!loading && isCompatible"
                :key="tab.comp"
                :config="parsedXML"  
                :vehicle-name="targetTitle"
                :device-model="deviceModel"
                :version="configVersion"
                :firmware-version="firmwareVersion"
                @settingsUpdated="settingsUpdated"
                @disableSendSave="disableSendSave"
              />
              <v-layout v-if="loading" align-center justify-center column class="push-down">
                <v-flex row align-center>
                  <v-progress-circular indeterminate :size="50" color="primary" class=""></v-progress-circular>
                </v-flex>
              </v-layout>
              <v-layout v-if="!isCompatible && !loading" align-center justify-center column class="push-down">
                <v-flex row align-center>
                  <v-icon color="red" x-large>
                    mdi-alert
                  </v-icon>
                </v-flex>
              </v-layout>
          </v-tab-item>
        </v-tabs>
      </v-form>
      <v-row v-if="!externalConfigMode">
        <v-col cols="5" class="remove-right-padding">
          <div class="config-status-container">
            <span>Status: {{ configStatus }}</span>
          </div>
        </v-col>
        <v-col cols="2">
          <div v-if="configType != ''" class="config-type-select">
            <v-radio-group v-model="configType">
              <v-radio
                v-for="option in configOptions"
                  :key="option.type"
                  :label="option.label"
                  :value="option.type"
                  :disabled="option.disabled"
                ></v-radio>
            </v-radio-group>
          </div>
          <div v-else class="config-type-select">
            <v-progress-circular indeterminate :size="30" color="primary" class=""></v-progress-circular>
          </div>
        </v-col>
        <v-col v-if="!standAlone" cols="5" class="remove-left-padding">
          <v-row class="row-action-container">
            <ConfigAction 
              :icon="'car'"
              :disabled="loading || !$can('retrieveConfigurationFromDevice')"
              label="Retrieve"
              arrow="right"
              @click.native="fetchCompatibleConfig"
            />
            <ConfigAction 
              :icon="'download'"
              :disabled="!hasConfig || disableSendSaveOptions"
              label="Save"
              @click.native="downloadXML"
            />
           <ConfigAction 
              :icon="'clipboard-list'"
              :disabled="!hasConfig || !isCompatible"
              label="Create Job"
              @click.native="toggleReview('createJob')"
            />
          </v-row>
          <v-row class="row-action-container">
            <ConfigAction 
              :icon="'car'"
              :disabled="loading || !isCompatible || !$can('sendConfigurationToDevice') || disableSendSaveOptions"
              arrow="left"
              label="Send"
              @click.native="toggleReview('updateConfig')"
            />

            <!-- Upload XML File -->
            <v-col cols="4" class="center-content">
              <v-btn
                text
                class="config-btn"
                :disabled="loading"
                @click.native="selectFile"
              >
                <v-row>
                  <v-col cols="12" class="config-action">
                    <v-icon>
                      mdi-file
                    </v-icon>
                    <input 
                      id="fileUpload" 
                      type="file" 
                      hidden
                      accept="text/xml"  
                      @change="loadConfigFile"
                    >
                  </v-col>
                  <v-col cols="12" class="config-action">
                    <span>Load</span>
                  </v-col>
                </v-row>
              </v-btn>
            </v-col>
            <!-- Upload End -->
            <ConfigAction 
              :icon="'undo'"
              label="Revert"
              :disabled="!hasConfig"
              @click.native="revertConfig"
            />
          </v-row>
        </v-col>

        <!-- Job specific config options -->
        <v-col v-else cols="5" class="remove-left-padding">
          <v-row class="row-action-container">
            <ConfigAction 
              :icon="'car'"
              :disabled="true"
              :width="3"
              label="Retrieve"
              arrow="right"
              @click.native="fetchCompatibleConfig"
            />
            <ConfigAction 
              :icon="'download'"
              :disabled="!hasConfig"
              :width="3"
              label="Save"
              @click.native="downloadXML"
            />
           <ConfigAction 
              :icon="'clipboard-list'"
              :disabled="true"
              :width="3"
              label="Create Job"
              @click.native="toggleReview('createJob')"
            />
            <v-spacer/>
            <ConfigAction 
              :icon="'check'"
              label="Done"
              :colour="'green'"
              :width="3"
              :disabled="!hasConfig"
              @click.native="toggleReview('proceedWithJob')"
            />
          </v-row>
          <v-row class="row-action-container">
            <ConfigAction 
              v-if="!loading && $can('sendConfigurationToDevice')"
              :icon="'car'"
              :disabled="true"
              :width="3"
              arrow="left"
              label="Send"
              @click.native="toggleReview('updateConfig')"
            />
            <v-col 
              v-else
              cols="4"
              class="center-content"
            >
              <v-progress-circular indeterminate :size="30" color="#0f74bb" class=""></v-progress-circular>
            </v-col>
            <!-- Upload XML File -->
            <v-col cols="3" class="center-content">
              <v-btn
                text
                class="config-btn"
                :disabled="loading"
                @click.native="selectFile"
              >
                <v-row>
                  <v-col cols="12" class="config-action">
                    <v-icon>
                      mdi-file
                    </v-icon>
                    <input 
                      id="fileUpload" 
                      type="file" 
                      hidden
                      accept="text/xml"  
                      @change="loadConfigFile"
                    >
                  </v-col>
                  <v-col cols="12" class="config-action">
                    <span>Load</span>
                  </v-col>
                </v-row>
              </v-btn>
            </v-col>
            <!-- Upload End -->
            <ConfigAction 
              :icon="'undo'"
              :width="3"
              label="Revert"
              :disabled="!hasConfig"
              @click.native="revertConfig"
            />
            <v-spacer/>
            <ConfigAction 
              :icon="'close'"
              :colour="'red'"
              label="Cancel"
              :width="3"
              :disabled="!valid"
              @click.native="closeWindow"
            />
          </v-row>
          
        </v-col>
      </v-row>
    </v-container>

    <CreateJob 
      v-if="!externalConfigMode"
      :open="displayCreateJob" 
      :mode="mode"
      :config="payload"
      :init-config="initConfig"
      :is-compatible="isCompatible"
      :compatibility-limit="compatibilityLimit"
      :compatibility-bounds="compatibilityBounds"
      :firmware-version="firmwareVersion"
      :config-type="configType"
      :changes="changes"
      :config-options-version-map="configOptionsVersionMap"
      @exit="displayCreateJob = false"
    />

    <ReviewChanges
      :show="toggleReviewChanges"
      :changes="changes"
      :config="initConfig"
      :context="context"
      :config-type="configType"
      @close="toggleReviewChanges = false"
      @createJob="toggleCreateJob"
      @updateConfig="updateConfig"
      @proceedWithJob="proceedWithConfigJob"
    />
  </v-card>
</template>

<script>

import { GetDeviceConfig, UpdateDeviceConfigOTA, GetDeviceInfo} from '@/services/API.js';

import {deviceConfigMixin} from '@/mixins/deviceConfigMixin.js';

import { checkNested } from '@/utils';
import { checkIfVersionIsAboveLimit } from '@/utils';

import Info from './sections/info.vue';
import Audio from './sections/audio.vue';
import CanBus from './sections/canBus.vue';
import Connection from './sections/connection.vue';
import VehicleInfo from './sections/vehicleInfo.vue';
import Alerts from './sections/alerts.vue';
import IO from './sections/IO.vue';
import Cameras from './sections/cameras.vue';
import Display from './sections/display.vue';
import Recording from './sections/recording.vue';
import DateTime from './sections/dateTime.vue';
import Power from './sections/power.vue';
import Admin from './sections/admin.vue';
import ConfigAction from './components/ConfigAction.vue';
import CreateJob from './components/CreateJob.vue';
import ReviewChanges from './components/ReviewChanges.vue';
import FirmwareUpdate from './sections/firmwareUpdate.vue';



import { Logger } from "aws-amplify";
const logger = new Logger("device config");

var xml2js = require('xml2js');
var set = require('lodash.set');
var unset = require('lodash.unset');

export default {
  name: 'DeviceConfig',
  components: {
    Info,
    Audio,
    Connection,
    VehicleInfo,
    Alerts,
    IO,
    Cameras,
    Display,
    Recording,
    DateTime,
    Power,
    Admin,
    ConfigAction,
    CreateJob,
    CanBus,
    ReviewChanges,
    FirmwareUpdate
  },
  mixins: [deviceConfigMixin],
  props: {
    item: {
      type: Object,
      required: true
    },
    org: {
      type: String,
      required: true
    },
    deviceId: {
      type: String,
      required: false,
      default: ''
    },
    standAlone: {
      type: Boolean,
      required: false,
      default: false
    },
    externalConfig: {
      type: String,
      required: false,
      default: ''
    }
  },
  data () {
    return {
      selectedTab: 0,
      valid: false,
      loading: true,
      configStatus: 'Loading...',
      parsedXML: null,
      formData: new FormData(),
      displayCreateJob: false,
      initDetails: {
        name: '',
        assigned_to: ''
      },
      open: false,
      mode: 'new',
      initConfig: null,
      encodedXML: '',
      fileName: '',
      initMileage: 0,
      versionSupportMap: {
        // D700
        'CAMERAMATICS_D700-SD-4G': {
          '1.0.0': {
            from: '0.00.00',
            to: '01.00.56'
          },
          '1.0.1': {
            from: '01.00.57',
            to: '9.99.99'
          },
          '1.0.2': {
            from: '01.01.01',
            to: '9.99.99'
          }
        },
        'CAMERAMATICS_JH4-HD-4G': {
          '1.0.0': {
            from: '0.00.00',
            to: '01.00.49'
          },
          '1.0.1': {
            from: '01.00.50',
            to: '9.99.99'
          },
          '1.0.2': {
            from: '01.00.50',
            to: '9.99.99'
          }
        },
        // Legacy 8 channel
        'CAMERAMATICS_JH8-HD-4G': {
          '1.0.0': {
            from: '0.00.00',
            to: '01.00.44'
          },
          '1.0.1': {
            from: '01.00.45',
            to: '9.99.99'
          },
          '1.0.2': {
            from: '01.00.45',
            to: '9.99.99'
          }
        },
        // Next Gen 8 channel
        'CAMERAMATICS_JH8_NT-HD-4G': {
          '1.0.0': {
            from: '0.00.00',
            to: '01.01.23'
          },
          '1.0.1': {
            from: '01.01.24',
            to: '9.99.99'
          },
          '1.0.2': {
            from: '01.01.93',
            to: '9.99.99'
          }
        },
        // Next gen 4 channel
        'CAMERAMATICS_JH4_NT-HD-4G': {
          '1.0.0': {
            from: '0.00.00',
            to: '01.01.23'
          },
          '1.0.1': {
            from: '01.01.24',
            to: '9.99.99'
          },
          '1.0.2': {
            from: '01.01.93',
            to: '9.99.99'
          }
        },
        
        'CAMERAMATICS_JI7A-SD-4G': {
          '1.0.0': {
            from: '0.00.00',
            to: '9.99.99'
          },
          '1.0.1': {
            from: '01.00.30',
            to: '9.99.99'
          }
        }
      },
      firmwareVersion: '',
      isCompatible: false,
      compatibilityLimit: {},
      compatibilityBounds: {},
      configLoadedFromFile: false,
      initDeviceModel: null,
      payloadConfig: {},
      changes: [],
      toggleReviewChanges: false,
      // Config type refers to a user sending a full config or a partial config
      configType: '',
      configOptionsVersionMap: {
        'CAMERAMATICS_D700-SD-4G': {
          from: '01.00.75',
          to: '9.99.99'
        },
        'CAMERAMATICS_JH4-HD-4G': {
          from: '01.00.54',
          to: '9.99.99'
        },
        'CAMERAMATICS_JH8-HD-4G': {
          from: '01.00.50',
          to: '9.99.99'
        },
        'CAMERAMATICS_JH8_NT-HD-4G': {
          from: '01.01.35',
          to: '9.99.99'
        },
        'CAMERAMATICS_JH4_NT-HD-4G': {
          from: '01.01.35',
          to: '9.99.99'
        },
        'CAMERAMATICS_JI7A-SD-4G': {
          from: '0.00.00',
          to: '9.99.99'
        }
      },
      context: '',
      supportsPartialConfig: true,
      disableSendSaveOptions: false
    }
  },
  computed: {
    targetTitle() {
      if (this.standAlone) return '';

      const vehicleName = this.item.vehicle ? this.item.vehicle : this.item.vehicle_name;
      const installTag = this.item.install_tag ? this.item.install_tag : 'Not Available';
      const imei = this.item.imei ? this.item.imei : 'Not Available';

      return `Vehicle: ${vehicleName}, Install Tag: ${installTag}, IMEI: ${imei}`;
    },
    primaryDeviceID() {
      if (this.standAlone) return '';

      if (this.deviceId.length > 0) {
        return this.deviceId;
      }
      else {
        let device = this.item.devices.find(device => {
          return device.is_defaultdevice === true || device.is_default_device === true || device.is_default_device === "true"
        });

        return device ? device.device_id : false;
      }
    },
    deviceModel() {
      if (!this.initDeviceModel) {
        return this.parsedXML ? this.parsedXML.device_config.target[0].model[0] : false;
      }
      else {
        return this.initDeviceModel;
      }
    },
    externalConfigMode() {
      return this.externalConfig.length > 0 ? true : false
    },
    hasConfig() {
      return this.parsedXML != null ? true : false;
    },
    configVersion() {
      if (!this.parsedXML) return null;

      const major = this.parsedXML.device_config.version[0].major[0];
      const minor = this.parsedXML.device_config.version[0].minor[0];
      const subminor = this.parsedXML.device_config.version[0].subminor[0];

      return `${major}.${minor}.${subminor}`;
    },
    supportsFirmwareUpdate() {
      if(!this.parsedXML) {
        return false
      }
      else if(this.parsedXML && Object.hasOwn(this.parsedXML.device_config.system[0], 'autoUpdate')) {
        return true
      }
      else {
        return false
      }
    },
    configTabs() {
      const tabs = [
        {
          comp: 'Info',
          label: 'Info',
          icon: 'mdi-account',
          supported: true
        },
        {
          comp: 'Audio',
          label: 'Audio Settings',
          icon: 'mdi-settings',
          supported: true
        },
        {
          comp: 'CanBus',
          label: 'CAN Bus',
          icon: 'mdi-car',
          supported: this.isTabSupportedByModel(this.deviceModel, 'CanBus')
        },
        {
          comp: 'Connection',
          label: 'Connection',
          icon: 'mdi-wifi',
          supported: true
        },
        {
          comp: 'VehicleInfo',
          label: 'Vehicle Info',
          icon: 'mdi-car',
          supported: true
        },
        {
          comp: 'Alerts',
          label: 'Alerts',
          icon: 'mdi-bell',
          supported: true
        },
        {
          comp: 'IO',
          label: 'I/O',
          icon: 'mdi-arrow-left-right',
          supported: true
        },
        {
          comp: 'Cameras',
          label: 'Cameras',
          icon: 'mdi-camera',
          supported: true
        },
        {
          comp: 'Display',
          label: 'Display',
          icon: 'mdi-monitor',
          supported: this.isSupporterByDevice(this.deviceModel)
        },
        {
          comp: 'Recording',
          label: 'Recording',
          icon: 'mdi-film',
          supported: true
        },
        {
          comp: 'DateTime',
          label: 'Date/Time',
          icon: 'mdi-calendar-range',
          supported: true 
        },
        {
          comp: 'Power',
          label: 'Power',
          icon: 'mdi-power',
          supported: true
        },
        {
          comp: 'FirmwareUpdate',
          label: 'Firmware Update',
          icon: 'mdi-head-cog',
          supported: this.supportsFirmwareUpdate
        },
        {
          comp: 'Admin',
          label: 'Admin',
          icon: 'mdi-shield',
          supported: true
        }
      ]

      return tabs.filter(tab => tab.supported == true)
    },
    version() {
      return this.parsedXML.device_config.version[0];
    },
    configOptions() {
      const options = [
        {
          label: 'Send only changes',
          type: 'partial',
          disabled: !this.supportsPartialConfig
        },
        {
          label: 'Send full config',
          type: 'full',
          disabled: false
        }
      ]

      return options;
    },
    payload() {
      const config = this.configType == 'full' ? this.parsedXML : this.payloadConfig;

      return config;
    }
  },
  watch: {
    selectedTab() {
      logger.info('selectedTab', this.selectedTab)
    },
    hasConfig() {
      if(this.hasConfig) {
        this.populateModelVersionInfo();
      }
    },
    compatibilityLimit(val) {
      this.$emit('compatibilityLimitUpdated', val);
    },
    configType(val) {
      if (val == 'full') {
        this.$emit('configTypeUpdated', false);
      }
      else if (val == 'partial') {
        this.$emit('configTypeUpdated', true);
      }
    }
  },  
  mounted() {
    this.setInitDeviceModel();

    if (this.standAlone && !this.externalConfigMode) {
      this.loading = false;
      this.configStatus = 'Please load config file'
      this.configType = 'partial';
    }
    else if (this.externalConfigMode && this.standAlone) {
      this.loadExternalConfig();
    }
    else {
      if (this.primaryDeviceID) {
        this.fetchCompatibleConfig();
      }
      else {
        this.loading = false;
        this.configStatus = 'Device missing from vehicle'
      }
    }
  },
  methods: {  
    disableSendSave(val) {
      this.disableSendSaveOptions = val
    },
    setInitconfigType() {
      logger.info('setInitconfigType ', this.supportsPartialConfig)

      this.supportsPartialConfig ? this.configType = 'partial' : this.configType = 'full';
    },
    isSupporterByDevice(model) {
      if(!model) return true

      if(model == 'CAMERAMATICS_JI7A-SD-4G') return false
      // default
      return true
    },
    isTabSupportedByModel(model, tab) {
      const map = {
        'CAMERAMATICS_D700-SD-4G': {
          'CanBus': true
        }
      }

      return checkNested(map, model, tab) ? map[model][tab] : false;
    },
    setInitDeviceModel() {
      if (Object.keys(this.item).length === 0) return;

      if (this.item.hasOwnProperty('model')) {
        this.initDeviceModel = this.item.model;

        return;
      }

      // attempt to find default device
      const device = this.item.devices.find(device => {
        return device.is_defaultdevice === true || device.is_default_device === true || device.is_default_device === "true"
      });

      if(device) {
        this.initDeviceModel = device.model;
      }
      else {
        // This is to catch device info passed in from fleet status page as it does not include an attribute
        // to indicate if a device is the default device
        if(this.item.devices.lenght > 0) {
          this.initDeviceModel = this.item.devices[0]
        } 
      }
      
    },
    fetchCompatibleConfig() {
      this.loading = true;

      Promise.all([
        this.getDeviceInfo(this.primaryDeviceID),
        this.fetchConfig(this.primaryDeviceID, 'xml')
      ])
      .then(res => {
        if (!this.parsedXML) return false;
        
        this.checkCompatibility();
        this.checkConfigOptionCompatibility();

        this.loading = false;
        logger.info('ready : ', res)
      })
      .catch(err => {
        this.loading = false;
        logger.info('there was an error ', err)
      });
    },
    async getDeviceInfo(id) {
      const deviceInfo = await GetDeviceInfo(id);
    
      if (deviceInfo) {
        this.firmwareVersion = deviceInfo.firmwareVersion;
        this.setInitconfigType();

        return deviceInfo;
      }
    },
    checkConfigOptionCompatibility() {
      logger.info('checkConfigOptionCompatibility ')
      if (this.firmwareVersion == '') {
        this.$emit('configOptionsVersionMap', this.configOptionsVersionMap);
        return false;
      }

      const aboveLimit = checkIfVersionIsAboveLimit(this.firmwareVersion, this.configOptionsVersionMap[this.deviceModel]['to']);
      const belowLimit = checkIfVersionIsAboveLimit(this.configOptionsVersionMap[this.deviceModel]['from'], this.firmwareVersion);

      if(!aboveLimit && !belowLimit) {
        this.supportsPartialConfig = true;
        this.setInitconfigType();
        logger.info('is true ', this.supportsPartialConfig)
      }
      else {
        this.supportsPartialConfig = false;
        this.setInitconfigType();
        logger.info('is false ', this.supportsPartialConfig)
      }
    },
    checkCompatibility() {
      if (!this.parsedXML) return;
      const model = this.parsedXML.device_config.target[0].model[0];

      // Check to see if firmware on DVR is compatible with config file

      if (!this.versionSupportMap[this.deviceModel][this.configVersion]) {
        this.configStatus = 'The firmware on this DVR is not compatible with this config file version';

        return;
      }
      this.compatibilityBounds = this.versionSupportMap[this.deviceModel];
      this.compatibilityLimit = this.versionSupportMap[this.deviceModel][this.configVersion];
    
      // firmwareVersion is firmware currently running on DVR 
      // compatibilityLimit is the upper limit as to what version of firmware the current model can accept
      const aboveLimit = checkIfVersionIsAboveLimit(this.firmwareVersion, this.compatibilityLimit.to);
      
      // In the case that a file is loaded from an external source, we need to check that it's actually suitable for the target device
      if (this.initDeviceModel && this.initDeviceModel != model) {
        this.isCompatible = false;
        this.configStatus = 'The firmware on this DVR is not compatible with this config file version';

        return;
      }
      if(aboveLimit) {
        this.isCompatible = false;
        this.configStatus = 'The firmware on this DVR is not compatible with this config file version';

        return;
      }
      else {
        this.isCompatible = true;

        return;
      }
    },
    
    populateModelVersionInfo() {
      const model = {
        newValue: this.deviceModel,
        key: 'device_config.target[0].model[0]'
      }
      const version = {
        newValue: this.version,
        key: 'device_config.version[0]'
      }

      this.settingsUpdated(model);
      this.settingsUpdated(version);

    },
    setInitMileage() {
      if (this.parsedXML) {
        this.initMileage = this.parsedXML.device_config.system[0].mileage[0];
      }
    },
    preventMileageUpdate() {
      const defaults = {
        newValue: -1,
        key: 'device_config.system[0].mileage[0]'
      };
      const currentMileage = parseInt(this.parsedXML.device_config.system[0].mileage[0]);
      const preventUpdate = currentMileage == parseInt(this.initMileage) ? true : false;
      
      if (preventUpdate) {
        set(this.parsedXML, defaults.key, defaults.newValue);
      }
    },
    toggleCreateJob() {
      this.context = '';
      this.displayCreateJob = !this.displayCreateJob;
    },
    selectFile() {
      document.getElementById("fileUpload").click()
    },
    loadExternalConfig() {
      this.loading = true;

      let decodedBase64 = atob(this.externalConfig);
      let xmlParser = new DOMParser();
      let xml = xmlParser.parseFromString(decodedBase64, "text/xml");
      let serialiser = new XMLSerializer();
      let xmlString = serialiser.serializeToString(xml);

      logger.debug("### fetch device config (loadExternalConfig)", xmlString)

      this.loading = false;
      this.configStatus = 'Config Loaded'
      this.configLoadedFromFile = true;
      
      // convert our xml data to JSON
      xml2js.parseString(xmlString, (err, result) => {
        if (result) {
          logger.debug("### fetch device config (loadExternalConfig)", result)
          this.initConfig = JSON.parse(JSON.stringify(result));
          this.parsedXML = JSON.parse(JSON.stringify(result));
         
          this.setInitMileage();
          this.checkCompatibility();
          this.checkConfigOptionCompatibility();
        }
      });
    },
    loadConfigFile() {
      this.parsedXML = null;
      this.initConfig = null;
      this.encodedXML = '';
      this.loading = true;

      const [file] = document.getElementById('fileUpload').files;
      const reader = new FileReader();
      
      reader.addEventListener("load", () => {
        this.loading = false;
        logger.info('loading from file')
        try {
          this.fileName = [file][0].name;
          this.parsedXML = this.uploadFileFromDisk(reader.result);
          this.initConfig = this.uploadFileFromDisk(reader.result);
          this.encodedXML = btoa(reader.result);
          this.configLoadedFromFile = true;
          this.configStatus = 'Config loaded from file';
          this.setInitMileage();
          this.checkCompatibility();
          this.checkConfigOptionCompatibility();
          this.$emit('fileName', this.fileName);
        }
        catch {
          this.configStatus = 'Error reading XML file'
        } 
      }, false);
      
      if (file) {
        reader.readAsText(file);
      }      
    },
    async fetchConfig(id, format) {
      this.parsedXML = null;
      let data = await GetDeviceConfig(id, format);
      if (data.device_config) {
        let decodedBase64 = atob(data.device_config);
        let xmlParser = new DOMParser();
        let xml = xmlParser.parseFromString(decodedBase64, "text/xml");
        let serialiser = new XMLSerializer();
        let xmlString = serialiser.serializeToString(xml);

        const errorCheck = xml.getElementsByTagName('parsererror')

        // Check for any errors in XML
        if(errorCheck.length > 0) {
          this.configStatus = 'Error reading XML file';
          throw new Error('Invalid XML');
        }

        logger.debug("### fetch device config (fetchConfig)", xmlString)

        this.configStatus = 'Config retrieved'
        
        // convert our xml data to JSON
        xml2js.parseString(xmlString, (err, result) => {
          if (result) {
            logger.info('### fetch device config (fetchConfig)"', result)
            this.initConfig = JSON.parse(JSON.stringify(result));
            this.parsedXML = JSON.parse(JSON.stringify(result));
            
            this.setInitMileage();
          }
        });

        return this.parsedXML;
      } 
      else {
        // force merge
        this.loading = false;
        this.configStatus = 'Device Not Online';
        this.parsedXML = null;

        return false;
      }
    },
    toggleReview(context) {
      this.context = context;
      this.toggleReviewChanges = !this.toggleReviewChanges;
    },  
    async updateConfig() {
      this.context = '';
      this.toggleReviewChanges = false;
      this.loading = true;
      this.valid = false;

      let xml = this.convertToXML();
      let base64 = btoa(xml);
      let payload = {
        'data': base64
      }

      this.preventMileageUpdate();
      logger.info('sent config : ', payload);
      let res = await UpdateDeviceConfigOTA(this.org, this.primaryDeviceID, 'xml', payload);

      if (res.result == "Success") {
        // this.valid = true;
        // this.loading = false;
        
        // this.initConfig = this.parsedXML;
        this.fetchCompatibleConfig()
        this.$emit('forceUpdateConfig');

        this.changes = [];
        this.populateModelVersionInfo();
      } else {
        this.loading = false;
        this.valid = false
      }
    },
    convertToXML(fullConfig = false) {
      let builder = new xml2js.Builder();
      logger.info('download xml ', fullConfig, this.parsedXML)
      let newXML = fullConfig ? builder.buildObject(this.parsedXML) : builder.buildObject(this.payload);

      return newXML;
    },
    settingsUpdated(data, map = []) {
      data.map = map;

      logger.debug("settings updated",data,map)
      
      // For the killswitch feature (WFT-1460), we have to delete properties from the global
      // XML config object for the first time. To do this, a string val of 'removePropertyFromConfigObject' is passed
      // as the newValue for a given property in the object. This will then use unset to delete that property.
      if(data.newValue == 'removePropertyFromConfigObject') {
        unset(this.payloadConfig, data.key);
        unset(this.parsedXML, data.key);
      } else {
        set(this.payloadConfig, data.key, data.newValue);
        set(this.parsedXML, data.key, data.newValue);
      }
      

      // IO pins & Camera Channels settings require an identifier, this is passed as the property 'context'
      if(data.hasOwnProperty('context')) {
        set(this.payloadConfig, data.context.key, data.context.newValue);
        set(this.parsedXML, data.context.key, data.context.newValue);
      }

      if(data.hasOwnProperty('parent')) {
        set(this.payloadConfig, data.parent.key, data.parent.newValue);
        set(this.parsedXML, data.parent.key, data.parent.newValue);
      }

      logger.info('json updated : ', this.payloadConfig);
      this.compileChanges(this.changes, data);
    },
    compileChanges(list, change) {
      logger.info('compileChanges ', list, change)
      // Update any previously modified fields
      const objIndex = list.findIndex((item => item.key == change.key));
      logger.info(objIndex)
      if (objIndex != -1) {
        list[objIndex].newValue = change.newValue;

        return;
      }

      // Add change details to list by default
      list.push(change);
    },
    closeWindow() {
      this.$emit('closeDeviceConfig');
    },
    downloadXML() {
      let xml = this.convertToXML(true);
      var filename = `${this.targetTitle}_config.xml`;
      var pom = document.createElement('a');
      var bb = new Blob([xml], {type: 'text/plain'});

      pom.setAttribute('href', window.URL.createObjectURL(bb));
      pom.setAttribute('download', filename);

      pom.dataset.downloadurl = ['text/plain', pom.download, pom.href].join(':');
      pom.draggable = true; 
      pom.classList.add('dragout');

      pom.click();
    },
    revertConfig() {
      this.parsedXML = this.initConfig;

      this.$emit('forceUpdateConfig');
    },
    proceedWithConfigJob() {
      this.context = '';
      let xml = this.convertToXML();
      let base64 = btoa(xml);

      this.$emit('proceedWithConfigJob', base64);
      this.closeWindow();
    }
  }
}

</script>

<style lang="scss" scoped>
  .device-config-parent {
    z-index: 9998;
    position: fixed;
    left: 0%;
    right: 0;
    top: 0;
    background-color: rgba(0, 0, 0, 0.4) !important;
    width: 100vw;
    height: 100vh;
    .device-content {
      background: white;
      margin: 0;
      position: relative;
      top: 50%;
      left: 50%;
      -ms-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
      padding: 0;
      overflow: hidden;
      border: solid grey 2px;
      width: 1100px;
      .config-status-container {
        padding: 2.5em 1em;
        padding-bottom: 3.5em;
        background: #e0e0e0;
      }

      .config-type-select {
        margin-top: 1em;
        border-right: solid #e0e0e0;
      }

      .config-btn {
        font-weight: 400;
        color: #0f74bb;
        padding: 0;
        height: 0;
        &:hover {
          cursor: pointer;
        }
      }
      .title-bar {
        background: #0f74bb;
        color: white;
        border-bottom: solid grey 2px;
        padding-top: 2em;
        padding-left: 2em;
      }
      .tab-container {
        padding-top: 12px;
        border-bottom: solid grey 2px;
      }
    }
    .close-icon {
      top: 0;
      right: 0;
      position: absolute;
    }
    .v-tab {
      justify-content: start !important;
    }
    .devive-config-tab {
      border-left: solid grey 2px !important;
      border-right: solid grey 2px !important;
    }
    .devive-config-tab:not(:first-child) {
      border-top: solid grey 2px;
    }
    .v-tab--active {
      background: #8fbaf6 !important;
    }
    .v-tabs--vertical > .v-window {
      border-left: solid grey 2px !important;
    }
  }
  .download-file {
    &:hover {
      cursor: pointer;
    }
  }
  
  .push-down {
    padding-top: 250px;
  }

  .remove-right-padding {
    padding-right: 0;
  }

  .remove-left-padding {
    padding-left: 0;
    margin-top: 0;
  }

  .row-action-container {
    margin: 0;
  }

  .config-action {
    padding: 0;
  }

  .center-content {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .config-btn {
    font-weight: 400;
    color: #0f74bb;
    padding: 0;
    height: 0;
    &:hover {
      cursor: pointer;
    }
  }

  .file-upload {
    display: block;
    text-align: center;
    padding-top: 0 !important;
  }

  .hidden {
    display: none !important;
  }
</style>